import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FlaskConical } from "lucide-react";
import { api } from "@/lib/api";
import { P05GroundTruthForm } from "@/components/P05GroundTruthForm";
import { P05ReviewPanel } from "@/components/P05ReviewPanel";
import { P05Report } from "@/components/P05Report";

const StepBadge = ({ done, label }) => (
  <Badge className={`text-[10px] ${done ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-600"}`}>{done ? "✔ " : ""}{label}</Badge>
);

export default function P05Pilot() {
  const [ov, setOv] = useState(null);
  const [sel, setSel] = useState(null);
  const [view, setView] = useState("gt");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => {
    api.get(`/p05/overview`).then((r) => setOv(r.data)).catch(() => toast.error("Errore overview P0.5"));
  }, []);
  useEffect(() => { load(); return () => timer.current && clearInterval(timer.current); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); okMsg && toast.success(okMsg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const runBatch = () => act(async () => {
    await api.post(`/p05/batch/run`);
    timer.current && clearInterval(timer.current);
    timer.current = setInterval(async () => {
      const r = await api.get(`/p05/batch/status`);
      setOv((o) => ({ ...o, batch: r.data }));
      if (r.data.status !== "running") { clearInterval(timer.current); load(); }
    }, 4000);
  }, "Batch cieco avviato in background");

  if (!ov) return <div className="p-4 text-sm" data-testid="p05-loading">Caricamento pilot P0.5…</div>;

  const batch = ov.batch || {};
  const allLocked = ov.gt_locked === ov.n_cases && ov.n_cases > 0;
  const batchDone = batch.status === "done";
  return (
    <div className="space-y-4 p-1" data-testid="p05-page">
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <FlaskConical size={15} /> P0.5 — Blind Generalization Pilot ({ov.classification})
        </CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex gap-1 flex-wrap" data-testid="p05-steps">
            <StepBadge done={ov.candidate_frozen} label="1. Versione congelata" />
            <StepBadge done={ov.inventory_closed} label="2. Inventario chiuso" />
            <StepBadge done={allLocked} label={`3. FASE A ${ov.gt_locked}/${ov.n_cases || 10}`} />
            <StepBadge done={batchDone} label="4. Batch cieco" />
            <StepBadge done={ov.ground_truth_opened} label="5. GT aperta" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {!ov.candidate_frozen && <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => act(() => api.post(`/p05/freeze`), "Versione candidata congelata")} data-testid="p05-freeze-btn">Congela versione candidata</Button>}
            {ov.candidate_frozen && !ov.inventory_closed && <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => act(() => api.post(`/p05/close-inventory`), "Inventario chiuso (10 casi)")} data-testid="p05-close-inventory-btn">Chiudi inventario (10 PDF)</Button>}
            {ov.inventory_closed && allLocked && !batchDone && batch.status !== "running" && <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={runBatch} data-testid="p05-run-batch-btn">Avvia batch cieco</Button>}
            {batch.status === "running" && <span className="text-xs animate-pulse" data-testid="p05-batch-progress">⏳ batch: {batch.current_case || "…"} ({batch.progress_pct}%)</span>}
            {batchDone && !ov.ground_truth_opened && <Button size="sm" variant="destructive" className="h-7 text-xs" disabled={busy} onClick={() => act(() => api.post(`/p05/open-ground-truth`), "Ground truth aperta: i 10 PDF sono ora casi di regressione")} data-testid="p05-open-gt-btn">Apri ground truth (irreversibile)</Button>}
          </div>
          {!ov.inventory_closed && ov.candidate_frozen && <p className="text-[11px] text-muted-foreground">L'inventario seleziona 10 PDF mai elaborati in modo deterministico (ordinamento sha256, content-blind) e non è più modificabile.</p>}
          {ov.inventory_closed && !allLocked && <p className="text-[11px] text-amber-800" data-testid="p05-gt-instruction">Compila e blocca la FASE A di TUTTI i casi guardando solo il disegno. Il batch parte solo a 10/10 bloccate.</p>}
        </CardContent>
      </Card>

      {ov.inventory_closed && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">Casi ciechi</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-[11px]" data-testid="p05-cases-table">
              <thead><tr className="text-left text-muted-foreground">
                <th className="pr-2 py-1">Caso</th><th className="pr-2">Pagine</th><th className="pr-2">Natura</th><th className="pr-2">FASE A</th><th className="pr-2">Output</th><th className="pr-2">Gate</th><th className="pr-2">Revisione</th><th>Azioni</th>
              </tr></thead>
              <tbody>
                {(ov.cases || []).map((c) => (
                  <tr key={c.neutral_id} className="border-t border-border" data-testid={`p05-case-${c.neutral_id}`}>
                    <td className="pr-2 py-1 font-medium">{c.neutral_id}</td>
                    <td className="pr-2">{c.pages}</td>
                    <td className="pr-2">{c.kind}</td>
                    <td className="pr-2">{c.gt_locked ? <Badge className="text-[9px] bg-emerald-100 text-emerald-800">bloccata</Badge> : (c.gt_draft ? "bozza" : "—")}</td>
                    <td className="pr-2">{c.frozen ? "congelato" : "—"}</td>
                    <td className="pr-2">{c.gate || "—"}</td>
                    <td className="pr-2">{c.review_completed ? "conclusa" : (c.review_started ? "in corso" : "—")}</td>
                    <td className="flex gap-1 py-0.5">
                      {!c.gt_locked && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setSel(c); setView("gt"); }} data-testid={`p05-open-gt-${c.neutral_id}`}>FASE A</Button>}
                      {c.frozen && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setSel(c); setView("review"); }} data-testid={`p05-open-review-${c.neutral_id}`}>Revisione</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {sel && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2">
            {sel.neutral_id} — {view === "gt" ? "FASE A (ground truth cieca)" : "Revisione tecnica misurata"}
            <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setSel(null)}>chiudi</Button>
          </CardTitle></CardHeader>
          <CardContent>
            {view === "gt" && <P05GroundTruthForm neutralId={sel.neutral_id} pages={sel.pages || 1} onLocked={load} />}
            {view === "review" && <P05ReviewPanel neutralId={sel.neutral_id} />}
          </CardContent>
        </Card>
      )}

      {ov.ground_truth_opened && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">Report e verdetto</CardTitle></CardHeader>
          <CardContent><P05Report /></CardContent>
        </Card>
      )}
    </div>
  );
}
